import { makeAutoObservable, runInAction } from 'mobx';

import { referralsService, type EveryoneFeedQuery } from '@/services/referrals';
import type { EveryoneFeedItem, EveryoneFeedResponse } from '@/types/referral';

const EMPTY_COUNTS: EveryoneFeedResponse['chip_counts'] = {
  all: 0,
  in_progress: 0,
  paid: 0,
  removed: 0,
};

/**
 * State for the Refer-tab "Who's clicked" surface. Server is the source
 * of truth for both items and chip_counts; this store just holds the
 * most recent server response and exposes load/refresh methods.
 *
 * v3 (2026-05-11): dropped `sourceCounts` (no source filter in v3) and
 * updated chip_counts shape to { all, in_progress, paid }.
 */
export class EveryoneFeedStore {
  items: EveryoneFeedItem[] = [];
  chipCounts: EveryoneFeedResponse['chip_counts'] = EMPTY_COUNTS;
  loaded = false;
  refreshing = false;
  refreshError: string | null = null;
  /**
   * True once a response arrives without `started` in chip_counts — a server
   * that predates the deal screen's tabs. Such a server filters
   * `stage=started` against a vocabulary it never had and returns nothing,
   * while the card synthesises a Started count from `in_progress`; the tab
   * then reads "8" over an empty list. Knowing this lets `fetch` ask in the
   * old vocabulary instead. Deploy order (backend first) should make this
   * moot; this is the guard for when it isn't.
   */
  legacyStages = false;
  /**
   * The deal and tab the current `items` belong to. A fetch for a different
   * scope drops the rows before the request goes out — see `fetch`.
   */
  private scopeDealId: number | null = null;
  private scopeStage: string | null = null;
  /** Last hide/unhide failure, surfaced as a toast by the Refer tab. */
  removeError: string | null = null;
  /**
   * Rows removed optimistically that are still showing their undo
   * tombstone. They stay in `items` at their original position until
   * `settleRemoval` drops them.
   */
  tombstoned = new Set<number>();
  /** Most recent query — coalesces successive identical in-flight requests. */
  private lastQueryKey: string | null = null;
  /**
   * Monotonic fetch id. A response is only applied if it belongs to the most
   * recent fetch. Debounced search fires overlapping requests ("a" then
   * "an"), and a pull-to-refresh can start before a hide() resolves and land
   * after it — either way a stale payload would clobber `items` and wipe an
   * open undo window.
   */
  private fetchEpoch = 0;
  /**
   * Referral ids with a hide/unhide in flight. Without this, tapping UNDO
   * inside the request round-trip lets unhide reach the server before hide,
   * leaving the row hidden while the UI says it came back.
   */
  private inFlight = new Set<number>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async fetch(query: EveryoneFeedQuery): Promise<void> {
    const key = serializeQuery(query);
    if (this.refreshing && this.lastQueryKey === key) return;

    // A new scope (another deal, or another tab) means the rows on screen
    // belong to the old one. Drop them now, not when the answer lands:
    // otherwise the new tab's panel sits over the previous tab's rows for
    // the length of a request, an empty Paid tab borrows Started's list,
    // and a deal opened after another shows the first deal's friends under
    // the second deal's header. A new deal also invalidates the counts.
    // Search keeps its rows: clearing on every keystroke would flash the
    // list empty between debounces.
    const dealId = query.dealId ?? null;
    const stage = query.stage ?? null;
    if (dealId !== this.scopeDealId || stage !== this.scopeStage) {
      this.items = [];
      this.loaded = false;
      this.tombstoned.clear();
      if (dealId !== this.scopeDealId) this.chipCounts = EMPTY_COUNTS;
      this.scopeDealId = dealId;
      this.scopeStage = stage;
    }

    this.refreshing = true;
    this.refreshError = null;
    this.lastQueryKey = key;
    const epoch = ++this.fetchEpoch;

    try {
      const sent = this.wireQuery(query);
      const response = await referralsService.everyoneFeed(sent);
      if (epoch !== this.fetchEpoch) return; // superseded — drop it

      // Learn the server's vocabulary from its answer. If this request
      // asked for a tab it turns out the server cannot serve, ask once
      // more in its own terms rather than showing a count over nothing.
      runInAction(() => {
        this.legacyStages = response.chip_counts.started === undefined;
      });
      const retry = this.wireQuery(query);
      if (retry.stage !== sent.stage) {
        const second = await referralsService.everyoneFeed(retry);
        if (epoch !== this.fetchEpoch) return;
        runInAction(() => this.applyResponse(second));
        return;
      }
      runInAction(() => this.applyResponse(response));
    } catch (err) {
      if (epoch !== this.fetchEpoch) return;
      runInAction(() => {
        this.refreshing = false;
        this.refreshError = err instanceof Error ? err.message : 'Failed to load funnel';
      });
    }
  }

  /**
   * Remove a dead lead from the ambassador's list.
   *
   * The row is NOT dropped from `items` — it's marked tombstoned and stays
   * at its position, where the card renders "Jake removed · Undo" in place.
   * Keeping it in the array means no index bookkeeping and no layout jump
   * while undo is still on offer. `settleRemoval` drops it once the window
   * closes.
   *
   * Optimistic: the tombstone appears before the request resolves, and a
   * failure puts the full row back. Returns false if the server refused
   * (e.g. the referee converted between render and tap, which is a 422).
   */
  async remove(referralId: number, dealId: number | null = null): Promise<boolean> {
    if (this.inFlight.has(referralId)) return false;
    this.inFlight.add(referralId);
    this.tombstoned.add(referralId);
    this.removeError = null;

    try {
      const response = await referralsService.hide(referralId, dealId);
      runInAction(() => {
        this.chipCounts = response.chip_counts;
      });

      // A 200 does not always mean "hidden". If a PerformCB postback marked
      // this referral paid between our write and the read-back, the server
      // clears hidden_at (paying always un-hides) and answers `hidden: false`.
      // Believing the status code over the payload would leave a tombstone on
      // a row that is actually paid and visible.
      if (!response.referral.hidden) {
        runInAction(() => {
          this.tombstoned.delete(referralId);
        });
        return false;
      }
      return true;
    } catch (err) {
      runInAction(() => {
        this.tombstoned.delete(referralId);
        this.removeError = err instanceof Error ? err.message : 'Could not remove';
      });
      return false;
    } finally {
      this.inFlight.delete(referralId);
    }
  }

  /** Undo a removal while the tombstone is still showing. */
  async undoRemoval(referralId: number, dealId: number | null = null): Promise<boolean> {
    if (this.inFlight.has(referralId)) return false;
    this.inFlight.add(referralId);
    const wasTombstoned = this.tombstoned.delete(referralId);
    this.removeError = null;

    try {
      const response = await referralsService.unhide(referralId, dealId);
      runInAction(() => {
        this.chipCounts = response.chip_counts;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        if (wasTombstoned) this.tombstoned.add(referralId);
        this.removeError = err instanceof Error ? err.message : 'Could not undo';
      });
      return false;
    } finally {
      this.inFlight.delete(referralId);
    }
  }

  /**
   * Undo window closed — drop the row for real. Purely local; the server
   * already knows. Idempotent, since the timer can race a pull-to-refresh
   * that already replaced `items`.
   */
  settleRemoval(referralId: number): void {
    if (!this.tombstoned.has(referralId)) return;
    this.tombstoned.delete(referralId);
    this.items = this.items.filter((item) => item.referral_id !== referralId);
  }

  isTombstoned(referralId: number): boolean {
    return this.tombstoned.has(referralId);
  }

  reset(): void {
    this.items = [];
    this.chipCounts = EMPTY_COUNTS;
    this.loaded = false;
    this.refreshing = false;
    this.refreshError = null;
    this.removeError = null;
    this.tombstoned.clear();
    this.inFlight.clear();
    this.lastQueryKey = null;
    this.legacyStages = false;
    this.scopeDealId = null;
    this.scopeStage = null;
  }

  /**
   * The query as the server we last spoke to understands it. To a server
   * from before the tabs split, the Started tab is `in_progress` — its
   * "everything unpaid", which is exactly what `tabCountsFor` shows for it.
   * Not started has no legacy equivalent and goes out unchanged; it reads 0
   * against that server, so an empty list is the truth there.
   */
  private wireQuery(query: EveryoneFeedQuery): EveryoneFeedQuery {
    if (this.legacyStages && query.stage === 'started') {
      return { ...query, stage: 'in_progress' };
    }
    return query;
  }

  private applyResponse(response: EveryoneFeedResponse): void {
    this.items = response.items;
    this.chipCounts = response.chip_counts;
    // Fresh server truth supersedes any in-flight undo window — the
    // removed rows simply aren't in this payload anymore.
    this.tombstoned.clear();
    this.loaded = true;
    this.refreshing = false;
  }
}

function serializeQuery(query: EveryoneFeedQuery): string {
  return JSON.stringify({
    d: query.dealId ?? null,
    st: query.stage ?? null,
    se: query.search?.trim() ?? null,
    // Part of the key so two queries that differ only in order never
    // coalesce into one in-flight request. Latent while only one sort is
    // ever sent; cheap to make impossible.
    so: query.sort ?? null,
  });
}

export const everyoneFeedStore = new EveryoneFeedStore();

/**
 * A SECOND, independent instance for the nudge screen.
 *
 * The nudge flow queries the same endpoint with a different question — scoped
 * to one deal, in_progress only — while the Refer tab asks an unscoped one.
 * Sharing a store means whichever screen fetched last wins, and the tab's
 * fetch effect keys on [stage, search], so it does NOT re-run on focus and
 * cannot correct itself: leave the nudge screen and "Who's clicked" keeps
 * rendering the deal-scoped result.
 *
 * This is a reasoned fix, not a reproduced one. Deal scoping arrived with the
 * nudge change, so the clobber is newly possible, but it was never observed:
 * the empty list seen while investigating turned out to be an unrelated 500,
 * and the isolation stands on its own merit rather than on that evidence.
 */
export const nudgeFeedStore = new EveryoneFeedStore();
